import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { trackPageView } from "@/lib/siteAnalytics";
import AdminAnalytics from "@/pages/AdminAnalytics";
import UnitTaskRunner from "@/pages/UnitTaskRunner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/components/AuthCallback";
import Landing from "@/pages/Landing";
import RoleSelect from "@/pages/RoleSelect";
import JourneyMap from "@/pages/JourneyMap";
import QuestView from "@/pages/QuestView";
import Leaderboard from "@/pages/Leaderboard";
import GalleryPage from "@/pages/GalleryPage";
import GuideConsole from "@/pages/GuideConsole";
import MockMeeting from "@/pages/MockMeeting";
import DocEditor from "@/pages/DocEditor";
import SpreadsheetEditor from "@/pages/SpreadsheetEditor";
import SlideBuilder from "@/pages/SlideBuilder";
import CipherPlayground from "@/pages/CipherPlayground";
import PhishingSpotter from "@/pages/PhishingSpotter";
import StudioHub from "@/pages/StudioHub";
import StudioMission from "@/pages/StudioMission";
import AssessmentRunner from "@/pages/AssessmentRunner";
import StudentReport from "@/pages/StudentReport";
import TrialEnded from "@/pages/TrialEnded";
import { Compass } from "lucide-react";

function Loader() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background">
      <Compass className="w-10 h-10 text-primary animate-spin" style={{ animationDuration: "3s" }} />
    </div>
  );
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.role) return <Navigate to="/welcome" replace />;
  if (user.trial && user.trial_expired) return <TrialEnded />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === "guide" ? "/guide" : "/map"} replace />;
  }
  if (user.trial && (location.pathname === "/leaderboard" || (location.pathname === "/map" && user.expedition_ids?.length))) {
    return <Navigate to="/studio/docs" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/welcome" element={<RoleSelect />} />
      <Route path="/admin/analytics" element={<Protected role="guide"><AdminAnalytics /></Protected>} />
      <Route path="/map" element={<Protected role="explorer"><JourneyMap /></Protected>} />
      <Route path="/quest/:questId" element={<Protected role="explorer"><QuestView /></Protected>} />
      <Route path="/lab/mock-meeting" element={<Protected role="explorer"><MockMeeting /></Protected>} />
      <Route path="/lab/doc-editor" element={<Protected role="explorer"><DocEditor /></Protected>} />
      <Route path="/lab/spreadsheet" element={<Protected role="explorer"><SpreadsheetEditor /></Protected>} />
      <Route path="/lab/slide-builder" element={<Protected role="explorer"><SlideBuilder /></Protected>} />
      <Route path="/lab/cipher" element={<Protected role="explorer"><CipherPlayground /></Protected>} />
      <Route path="/lab/phishing" element={<Protected role="explorer"><PhishingSpotter /></Protected>} />
      <Route path="/studio/:track" element={<Protected><StudioHub /></Protected>} />
      <Route path="/studio/:track/:missionId" element={<Protected><StudioMission /></Protected>} />
      <Route path="/assessment/:assessmentId" element={<Protected><AssessmentRunner /></Protected>} />
      <Route path="/unit-task/:taskId" element={<Protected><UnitTaskRunner /></Protected>} />
      <Route path="/report/:userId" element={<Protected role="guide"><StudentReport /></Protected>} />
      <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
      <Route path="/gallery" element={<Protected><GalleryPage /></Protected>} />
      <Route path="/guide" element={<Protected role="guide"><GuideConsole /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App hq-noise">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
